import { HUD } from "odyssey-shared";
import { type ReactElement } from "react";
import { Glyph, HudFrame, logical } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { slotRect } from "./area-slots.ts";
import { HUD_SLOTS, type HudArea } from "./slots.ts";
import type { HudConnection } from "./store.ts";

/**
 * The connection mark (P1.9-T32, P1.9-T46; the design's `ConnectionMark`): a quiet chip in the HUD frame, in its slot
 * under the player card, in four states. Reconnecting says the page is trying again in the background and which attempt
 * of how many; Connected says the connection is back; Slow connection stands alone; Offline
 * says retries have stopped. It never covers play and takes no pointer; it stands in the place of the player's status
 * effects while it shows, since a held world's effects are stale. Each state has its own glyph and colour and says
 * its word as well, so colour is never the only sign.
 */

/**
 * How one state draws: its glyph, colour and whether the glyph pulses.
 */
interface MarkLook {
  readonly glyph: "unlink" | "check" | "hourglass";
  readonly colour: string;
  readonly pulses: boolean;
}

const LOOKS: Readonly<Record<HudConnection["state"], MarkLook>> = {
  reconnecting: { glyph: "unlink", colour: "var(--ot-warning)", pulses: true },
  restored: { glyph: "check", colour: "var(--ot-positive)", pulses: false },
  slow: { glyph: "hourglass", colour: "var(--ot-warning)", pulses: false },
  offline: { glyph: "unlink", colour: "var(--ot-danger)", pulses: false }
};

/**
 * The words and the extra figure a state shows.
 *
 * @param connection What the mark shows.
 * @returns The word, and the detail beside it or null for none.
 */
export function connectionWords(connection: HudConnection): { readonly word: string; readonly detail: string | null } {
  switch (connection.state) {
    case "reconnecting":
      return { word: say("hud.connection.reconnecting"), detail: say("hud.connection.attempt", { attempt: String(connection.attempt), total: String(connection.total) }) };
    case "restored":
      return { word: say("hud.connection.restored"), detail: null };
    case "slow":
      return { word: say("hud.connection.slow"), detail: null };
    case "offline":
      return { word: say("hud.connection.offline"), detail: null };
  }
}

/**
 * What the connection mark does to the player's status effects. A reconnect's mark (Reconnecting, Connected) stands in
 * their place, since a held world's effects are stale. Slow connection and Offline leave them true, so the row stays
 * and drops beneath the mark: the target's frame and its effects begin 144 logical pixels from the edge, where a row
 * beside the mark would meet them, so the row is stacked, not shifted right.
 *
 * @param connection What the mark shows, or null for none.
 * @returns Whether the row is hidden (`held`) and how far under its slot it is drawn (`drop`, in logical pixels).
 */
export function statusUnderMark(connection: HudConnection | null): { readonly held: boolean; readonly drop: number } {
  if (connection === null) {
    return { held: false, drop: 0 };
  }
  if (connection.state === "slow" || connection.state === "offline") {
    return { held: false, drop: HUD_SLOTS.connection.height + HUD.gutter };
  }
  return { held: true, drop: 0 };
}

/**
 * The mark in its slot.
 *
 * @param props The `area` the HUD is laid out on and what the mark shows, `connection`.
 * @returns The mark, or nothing while the connection holds.
 */
export function ConnectionMark({ area, connection }: { readonly area: HudArea; readonly connection: HudConnection | null }): ReactElement | null {
  if (connection === null) {
    return null;
  }
  const rect = slotRect("connection", area);
  const { word, detail } = connectionWords(connection);
  const look = LOOKS[connection.state];
  return (
    <div className="ot-hud-slot ot-hud-connection" data-hud-slot="connection" data-state={connection.state} role="status" aria-live="polite" aria-label={detail === null ? word : `${word}, ${detail}`} style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) }}>
      <HudFrame>
        <span className="ot-hud-connection__row" style={{ color: look.colour }}>
          <span className={look.pulses ? "ot-hud-connection__glyph is-pulsing" : "ot-hud-connection__glyph"}>
            <Glyph name={look.glyph} />
          </span>
          <span className="ot-hud-connection__word">{word}</span>
          {detail === null ? null : <span className="ot-hud-connection__detail">{detail}</span>}
        </span>
      </HudFrame>
    </div>
  );
}
